import { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import { Button, Group, Modal, Select, TextInput, Switch, Grid, Table, Text, Title, Divider, ScrollArea, ActionIcon, Stack, Paper, Input } from "@mantine/core";
import { IconPlus, IconX, IconSearch } from "@tabler/icons-react";
import { UserType, AddUpdateUserRequestType } from "../../../../types/User";
import { addUpdateUser } from "../../../../services/UserService";
import useTransientStore from "../../../../data/transcientStore";
import { Role } from "../../../../types/Role";
import { notifications } from "@mantine/notifications";
import { ApplicationAddressSystemType } from "../../../../types/ApplicationAddressSystem";
import styles from './ModalForms.module.css';
import { useTranslation } from "../../../../hooks/useTranslation";

interface AddUserFormProps {
    opened: boolean;
    open: () => void;
    close: () => void;
    roles: Role[];
    onSuccess: () => void;
    allUsers: UserType[]
}

interface EditUserFormProps extends AddUserFormProps {
    selectedUser: UserType
}

const exceptionEmails = [
    'ari.hyvonen@ramboll.fi',
    'prashant.gurung@ramboll.co.uk'
];

export function AddUserForm({ opened, open, close, roles, allUsers, onSuccess }: AddUserFormProps) {
  const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({ pageId: 312, addressSystemId: addrIdFromStore || 0, autoLoad: true });
    const organisations = useTransientStore((state) => state.allOrganisations);
    const addressSystems = useTransientStore((state) => state.allAddressSystems);
    const organisationLabels = organisations.map((org) => org.name);
    const roleLabels = roles.map((role) => role.name);
    const addUserForm = useForm({
        mode: 'controlled',
        initialValues: {
            id: null,
            email: '',
            name: '',
            username: '',
            roleId: 0,
            organisationId: 0,
            addressSystemId: [] as number[],
            active: 1 
        },

        validate: {
            email: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_USER_FORM_EMAIL_REQUIRED") || "Please enter your email address.";

                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) return getTranslation("LABEL_USER_FORM_EMAIL_INVALID") || "Please enter a valid email address.";

                const isExceptionEmail = exceptionEmails.includes(value.toLowerCase().trim());
                if (!isExceptionEmail && 
                    allUsers?.some(user => user.email?.toLowerCase().trim() === value.toLowerCase().trim())) {
                    return getTranslation("LABEL_USER_FORM_EMAIL_TAKEN") || "This email address is already in use.";
                }
                return null;
            },
            name: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_USER_FORM_NAME_REQUIRED") || "Please enter a name.";
                return null;
            },
            username: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_USER_FORM_USERNAME_REQUIRED") || "Please enter a username.";

                if (allUsers?.some(user => user.username?.toLowerCase().trim() === value.toLowerCase().trim())) {
                    return getTranslation("LABEL_USER_FORM_USERNAME_TAKEN") || "This username is already taken.";
                }
                return null;
            },
            roleId: (value: number) => (value <= 0 ? (getTranslation("LABEL_USER_FORM_ROLE_REQUIRED") || "Please select a role") : null),
            organisationId: (value: number) => (value <= 0 ? (getTranslation("LABEL_USER_FORM_ORG_REQUIRED") || "Please select an organisation.") : null),
        }
    })

    function handleRoleChange(clickedRole: string) {
        const foundRole = roles.find((role) => role.name === clickedRole);
        const foundId = foundRole ? foundRole.id : 0;
        addUserForm.setFieldValue('roleId', foundId);
    }

    function handleOrgChange(clickedOrg: string) {
        const foundOrg = organisations.find((org) => org.name === clickedOrg);
        const foundId = foundOrg ? foundOrg.id : 0;
        addUserForm.setFieldValue('organisationId', foundId);
    }

    return (
        <>
            <Modal opened={opened} onClose={() => { addUserForm.reset(); close() }} title={getTranslation("TITLE_ADD_USER") || "Add User"} centered radius={'md'} size="xl">
                <form onSubmit={addUserForm.onSubmit(async (values) => {
                    const payload: AddUpdateUserRequestType = {
                        id: values.id,
                        email: values.email,
                        name: values.name,
                        username: values.username,
                        roleId: values.roleId,
                        organisationId: values.organisationId,
                        addressSystemId: values.addressSystemId,
                        active: values.active
                    };
                    await addUpdateUser(payload);
                    addUserForm.reset();
                    close();
                    onSuccess();
                    notifications.show({
                        title: getTranslation("LABEL_USER_FORM_SUCCESS_TITLE") || "Success",
                        message: getTranslation("LABEL_USER_FORM_SUBMITTED") || "User Successfully Submitted.",
                        color: 'green',
                        withBorder: true,
                        withCloseButton: true,
                        autoClose: 5000,
                        className: 'notification-success'
                        // position: 'top-center'
                    })
                })}>
                    <Grid>
                        <Grid.Col span={6}>
                        <TextInput
                            withAsterisk
                            label={getTranslation("LABEL_EMAIL") || "Email"}
                            placeholder={getTranslation("PLACEHOLDER_ENTER_EMAIL") || "Enter Email"}
                            key={addUserForm.key('email')}
                            {...addUserForm.getInputProps('email')}
                        />

                        <TextInput
                            withAsterisk
                            label={getTranslation("LABEL_NAME") || "Name"}
                            placeholder={getTranslation("PLACEHOLDER_ENTER_NAME") || "Enter Name"}
                            key={addUserForm.key('name')}
                            {...addUserForm.getInputProps('name')}
                        />

                        <TextInput
                            withAsterisk
                            label={getTranslation("LABEL_USERNAME") || "Username"}
                            placeholder={getTranslation("PLACEHOLDER_ENTER_USERNAME") || "Enter Username"}
                            key={addUserForm.key('username')}
                            {...addUserForm.getInputProps('username')}
                        />

                        {/* <TextInput
                            withAsterisk
                            label={getTranslation("LABEL_ROLE_ID") || "Role ID"}
                            placeholder={getTranslation("PLACEHOLDER_ENTER_ROLE_ID") || "Enter Role ID"}
                            key={addUserForm.key('roleId')}
                            {...addUserForm.getInputProps('roleId')}
                        /> */}

                        <Select
                            withAsterisk
                            label={getTranslation("LABEL_ROLE") || "Role"}
                            placeholder={getTranslation("PLACEHOLDER_SELECT_ROLE") || "Select Role"}
                            data={roleLabels}
                            value={roles.find(r => r.id === addUserForm.values.roleId)?.name || ''}
                            onChange={(value) => handleRoleChange(value || '')}
                            error={addUserForm.errors.roleId}
                        />

                        <Select
                            withAsterisk
                            label={getTranslation("LABEL_ORGANISATION") || "Organisation"}
                            placeholder={getTranslation("PLACEHOLDER_SELECT_ORGANISATION") || "Select Organisation"}
                            data={organisationLabels}
                            value={organisations.find(o => o.id === addUserForm.values.organisationId)?.name || ''}
                            onChange={(value) => handleOrgChange(value || '')}
                            error={addUserForm.errors.organisationId}
                            searchable
                        />

                        {/* <SystemSelector
                            addressSystems={addressSystems}
                            selectedSystemIds={addUserForm.values.addressSystemId}
                            onSelectionChange={(selectedIds) => 
                                addUserForm.setFieldValue("addressSystemId", selectedIds)
                            }
                        /> */}
                        </Grid.Col>
                        <Grid.Col span={6}>
                            <SystemSelector
                                addressSystems={addressSystems}
                                selectedSystemIds={addUserForm.values.addressSystemId}
                                onSelectionChange={(selectedIds) => addUserForm.setFieldValue("addressSystemId", selectedIds)}
                            />
                        </Grid.Col>
                    </Grid>

                    <Group justify="flex-end" mt="md">
                        <Button type="submit">{getTranslation("LABEL_USER_FORM_SUBMIT") || "Submit"}</Button>
                    </Group>
                </form>
            </Modal>
        </>
    )
}

export function EditUserForm({ opened, open, close, roles, selectedUser, allUsers, onSuccess }: EditUserFormProps) {
    const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
    const addrIdFromStore = selectedAddressSystem?.id || 0;
    const { getTranslation } = useTranslation({ pageId: 312, addressSystemId: addrIdFromStore || 0, autoLoad: true });
    const organisations = useTransientStore((state) => state.allOrganisations);
    const addressSystems = useTransientStore((state) => state.allAddressSystems);
    const organisationLabels = organisations.map((org) => org.name);
    const roleLabels = roles.map((role) => role.name);
    const addressSystemLabels = addressSystems.map((system) => system.name).sort();

    const editUserForm = useForm({
        mode: "controlled",
        initialValues: {
            email: "",
            name: "",
            username: "",
            roleId: 0,
            organisationId: 0,
            addressSystemId: [] as number[],
            active: 1
        },
        validate: {
            email: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_USER_FORM_EMAIL_REQUIRED") || "Please enter your email address.";

                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) return getTranslation("LABEL_USER_FORM_EMAIL_INVALID") || "Please enter a valid email address.";

                const isExceptionEmail = exceptionEmails.includes(value.toLowerCase().trim());
                if (!isExceptionEmail && 
                    allUsers?.some(user => user.email?.toLowerCase().trim() === value.toLowerCase().trim() && user.id !== selectedUser.id)) {
                    return getTranslation("LABEL_USER_FORM_EMAIL_TAKEN") || "This email address is already in use.";
                }
                return null;
            },
            name: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_USER_FORM_NAME_REQUIRED") || "Please enter a name.";
                return null;
            },
            username: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_USER_FORM_USERNAME_REQUIRED") || "Please enter a username.";

                if (allUsers?.some(user => user.username?.toLowerCase().trim() === value.toLowerCase().trim() && user.id !== selectedUser.id)) {
                    return getTranslation("LABEL_USER_FORM_USERNAME_TAKEN") || "This username is already taken.";
                }
                return null;
            },
            roleId: (value: number) => (value <= 0 ? (getTranslation("LABEL_USER_FORM_ROLE_REQUIRED") || "Please select a role") : null),
            organisationId: (value: number) => (value <= 0 ? (getTranslation("LABEL_USER_FORM_ORG_REQUIRED") || "Please select an organisation.") : null),
        }
    })

    useEffect(() => {
        if (opened && selectedUser) {
            const organisation = organisations.find((org) => org.id === selectedUser.organisationId);
            const role = roles.find((role) => role.id === selectedUser.role.id);

            editUserForm.setValues({
                email: selectedUser.email || "",
                name: selectedUser.name || "",
                username: selectedUser.username || "",
                roleId: role ? role.id : 0,
                organisationId: organisation ? organisation.id : 0,
                addressSystemId: selectedUser.addresses?.map(address => address.id) || [], // 
                active: selectedUser.active
            });
        }
    }, [opened, selectedUser, organisations, roles, addressSystems]);

    return (
        <Modal
            opened={opened}
            onClose={() => {
                editUserForm.reset();
                close();
            }}
            title={getTranslation("TITLE_EDIT_USER") || "Edit User"}
            centered
            size="xl"
            radius={'md'}
        >
            <form
                onSubmit={editUserForm.onSubmit(async (values) => {
                    const payload: AddUpdateUserRequestType = {
                        id: selectedUser.id,
                        email: values.email,
                        name: values.name,
                        username: values.username,
                        roleId: values.roleId,
                        organisationId: values.organisationId,
                        addressSystemId: values.addressSystemId,
                        active: values.active
                    };
                    await addUpdateUser(payload);
                    editUserForm.reset();
                    close();
                    onSuccess();
                    notifications.show({
                        title: getTranslation("LABEL_USER_FORM_SUCCESS_TITLE") || "Success",
                        message: getTranslation("LABEL_USER_FORM_UPDATED") || "User Successfully Updated.",
                        color: "green",
                        withBorder: true,
                        withCloseButton: true,
                        autoClose: 5000,
                        className: 'notification-success'
                        // position: "top-center",
                    });
                })}
            >
                {/* <Paper p="sm" withBorder> */}
                <Grid>
                    <Grid.Col span={6}>
                        <TextInput
                            withAsterisk
                            label={getTranslation("LABEL_EMAIL") || "Email"}
                            placeholder={getTranslation("PLACEHOLDER_ENTER_EMAIL") || "Enter Email"}
                            {...editUserForm.getInputProps("email")}
                        />

                        <TextInput
                            withAsterisk
                            label={getTranslation("LABEL_NAME") || "Name"}
                            placeholder={getTranslation("PLACEHOLDER_ENTER_NAME") || "Enter Name"}
                            key={editUserForm.key('name')}
                            {...editUserForm.getInputProps('name')}
                            mt="md"
                        />

                        <TextInput
                            withAsterisk
                            label={getTranslation("LABEL_USERNAME") || "Username"}
                            placeholder={getTranslation("PLACEHOLDER_ENTER_USERNAME") || "Enter Username"}
                            {...editUserForm.getInputProps("username")}
                            mt="md"
                        />

                        <Select
                            withAsterisk
                            label={getTranslation("LABEL_ROLE") || "Role"}
                            placeholder={getTranslation("PLACEHOLDER_SELECT_ROLE") || "Select Role"}
                            data={roleLabels}
                            value={roles.find((role) => role.id === editUserForm.values.roleId)?.name || ""}
                            onChange={(value) => {
                                const foundRole = roles.find((role) => role.name === value);
                                editUserForm.setFieldValue("roleId", foundRole ? foundRole.id : 0);
                            }}
                            error={editUserForm.errors.roleId}
                            mt="md"
                        />

                        <Select
                            withAsterisk
                            label={getTranslation("LABEL_ORGANISATION") || "Organisation"}
                            placeholder={getTranslation("PLACEHOLDER_SELECT_ORGANISATION") || "Select Organisation"}
                            data={organisationLabels}
                            value={organisations.find((org) => org.id === editUserForm.values.organisationId)?.name || ""}
                            onChange={(value) => {
                                const foundOrg = organisations.find((org) => org.name === value);
                                editUserForm.setFieldValue("organisationId", foundOrg ? foundOrg.id : 0);
                            }}
                            error={editUserForm.errors.organisationId}
                            searchable
                            mt="md"
                        />

                        <Group align="center" mt="md">
                            <Text fw={500} fz="sm">{getTranslation("LABEL_USER_FORM_ARCHIVED") || "Archived"}</Text>
                            <Switch
                                checked={editUserForm.values.active === 0}
                                onChange={(event) => editUserForm.setFieldValue('active', event.currentTarget.checked ? 0 : 1)}
                                styles={(theme) => ({
                                    track: {
                                        backgroundColor: editUserForm.values.active === 1 ? 'gray' : undefined,
                                        borderColor: editUserForm.values.active === 1 ? 'gray' : undefined,
                                    }
                                })}
                            />
                        </Group>
                    </Grid.Col>

                    <Grid.Col span={6}>
                        <SystemSelector
                            addressSystems={addressSystems}
                            selectedSystemIds={editUserForm.values.addressSystemId}
                            onSelectionChange={(selectedIds) => editUserForm.setFieldValue("addressSystemId", selectedIds)}
                        />
                    </Grid.Col>
                </Grid>

                <Group justify="flex-end" mt="md">
                        <Button type="submit">{getTranslation("LABEL_USER_FORM_SUBMIT") || "Submit"}</Button>
                </Group>
                {/* </Paper> */}
            </form>
        </Modal>
    );
}

interface ViewUserFormProps {
    opened: boolean;
    close: () => void;
    selectedUser: UserType;
}

export function ViewUserForm({ opened, close, selectedUser }: ViewUserFormProps) {
    const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
    const addrIdFromStore = selectedAddressSystem?.id || 0;
    const { getTranslation } = useTranslation({ pageId: 312, addressSystemId: addrIdFromStore || 0, autoLoad: true });

    return (
        <Modal
            opened={opened}
            onClose={close}
            title={getTranslation("TITLE_ADDRESS_SYSTEMS") || "Address Systems"}
            centered
            size="xs"
            radius={'md'}
        >
            <div className={styles.addressSystemTable}>
                <Table>
                    <Table.Thead className={styles.stickyHeader}>
                        <Table.Th className={styles.headerCell}>
                            {getTranslation("LABEL_USER_FORM_ADDRESS_SYSTEM_NAME") || "Address System Name"}
                        </Table.Th>
                    </Table.Thead>
                    <Table.Tbody>
                        {selectedUser.addresses.length > 0 ? (
                            selectedUser.addresses
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((system) => (
                                    <Table.Tr key={system.id} className={styles.noBorderRow}>
                                        <Table.Td>{system.name}</Table.Td>
                                    </Table.Tr>
                                ))
                        ) : (
                            <Table.Tr>
                                <Table.Td>
                                    <Text size="sm" c="dimmed" ta="center">
                                        {getTranslation("LABEL_USER_FORM_NO_ADDRESS_SYSTEMS") || "No address systems assigned to this user"}
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                        )}
                    </Table.Tbody>
                </Table>
            </div>
        </Modal>
    );
}

interface SystemSelectorProps {
    addressSystems: ApplicationAddressSystemType[];
    selectedSystemIds: number[];
    onSelectionChange: (selectedIds: number[]) => void;
}

function SystemSelector({ addressSystems, selectedSystemIds, onSelectionChange }: SystemSelectorProps) {
    const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
    const addrIdFromStore = selectedAddressSystem?.id || 0;
    const { getTranslation } = useTranslation({ pageId: 312, addressSystemId: addrIdFromStore || 0, autoLoad: true });
    const [selectedSystemName, setSelectedSystemName] = useState<string | null>(null);

    const availableSystems = addressSystems.filter(
        system => !selectedSystemIds.includes(system.id)
    ).sort((a, b) => a.name.trim().localeCompare(b.name.trim()));

    const selectedSystems = addressSystems.filter(system => 
        selectedSystemIds.includes(system.id)
    );

    const handleAddSystem = () => {
        const system = availableSystems.find(sys => sys.name.trim() === selectedSystemName);
        if (!system || selectedSystemIds.includes(system.id)) return;
        
        const newSelection = [...selectedSystemIds, system.id];
        onSelectionChange(newSelection);
        setSelectedSystemName(null);
    };

    const handleRemoveSystem = (systemId: number) => {
        const newSelection = selectedSystemIds.filter(id => id !== systemId);
        onSelectionChange(newSelection);
    };

    return (
        <Stack gap="sm">
            <Group gap="xs" align="end" className={styles.formGroup}>
                <Select
                    placeholder={getTranslation("PLACEHOLDER_SELECT_AN_ADDRESS_SYSTEM") || "Select an address system..."}
                    data={availableSystems.map(system => system.name.trim())}
                    value={selectedSystemName}
                    onChange={setSelectedSystemName}
                    rightSection={<IconSearch size={18} color="#009DF0" />}
                    className={styles.flexInput}
                    size="sm"
                    searchable
                    clearable
                    label={getTranslation("LABEL_ADDRESS_SYSTEMS") || "Address Systems"}
                />

                <Button
                    onClick={handleAddSystem}
                    disabled={!selectedSystemName || !availableSystems.some(sys => sys.name.trim() === selectedSystemName)}
                    variant="filled"
                    size="sm"
                >
                    {getTranslation("LABEL_USER_FORM_ADD") || "Add"}
                </Button>
            </Group>

            {selectedSystems.length > 0 && (
                <Paper p="sm" withBorder>
                    <div className={styles.scrollableRoleTable}>
                        <Table highlightOnHover className={styles.roleTable}>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th className={styles.stickyRoleHeader}>
                                        {getTranslation("LABEL_USER_FORM_ADDRESS_SYSTEM_NAME") || "Address System Name"}
                                    </Table.Th>
                                    <Table.Th
                                        w={60}
                                        className={styles.stickyRoleHeader}
                                    >
                                        {getTranslation("LABEL_USER_FORM_REMOVE") || "Remove"}
                                    </Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {selectedSystems
                                    .sort((a, b) => a.name.trim().localeCompare(b.name.trim()))
                                    .map((system) => (
                                        <Table.Tr key={system.id}>
                                            <Table.Td>{system.name.trim()}</Table.Td>
                                            <Table.Td className={styles.centerCell}>
                                                <ActionIcon
                                                    onClick={() => handleRemoveSystem(system.id)}
                                                    variant="subtle"
                                                    color="red"
                                                    size="sm"
                                                >
                                                    <IconX size={18} />
                                                </ActionIcon>
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                            </Table.Tbody>
                        </Table>
                    </div>
                </Paper>
            )}
        </Stack>
    );
}